import type {
  GetSchoolDetails,
  Trial,
  GetSupportDistrict,
  DistrictCreateSchool,
  NewDistrict
} from "@/api_types/school";
import FetchService from "@/services/fetch.service";
import { asyncComputed } from "@/utils/asyncComputed";
import { sort } from "@/utils/sort";
import { defineStore } from "pinia";

export const useSchools = defineStore("support/schools", {
  state: () => {
    return {
      schools: asyncComputed(
        async (controller) => {
          const res = (await FetchService.fetchData(
            "/schools",
            "get",
            undefined,
            undefined,
            controller
          )) as (GetSchoolDetails & Record<string, any>)[];

          return sort(res || [], (i) => i.school_name);
        },
        [],
        undefined,
        false,
        []
      ),
      districts: asyncComputed(
        async (controller) => {
          const res = (await FetchService.fetchData(
            "/districts",
            "get",
            undefined,
            undefined,
            controller
          )) as GetSupportDistrict[];
          return sort(res || [], (i) => i.district_name);
        },
        [],
        undefined,
        false,
        []
      )
    };
  },
  getters: {
    getDistrict: (state) => {
      return (district_id) => {
        const district = Object.values(state.districts.data).find(
          (district) => district.district_id == district_id
        );

        if (district) {
          return district.schools.find((school) => school.district === true);
        }
        
        return null;
      };
        // Object.values(state.districts.data).find((district: GetSupportDistrict) => district.district_id == district_id)?.schools.find((school) => school.district);
    }
  },
  actions: {
    async updateSchool(school_id, schoolData: Partial<GetSchoolDetails>) {
      const res = await FetchService.fetchData(`/schools/${school_id}`, "put", schoolData);
      if (res?.school_name) {
        this.schools.data.forEach((g) => {
          if (g.school_id === school_id) {
            Object.assign(g, res);

            // Keep local nested status fields in sync when backend response is partial.
            if (schoolData?.status) {
              g.status = {
                ...(g.status || {}),
                ...schoolData.status,
                ...(res?.status || {})
              };
            }
          }
        });
        return true;
      }
      return false;
    },
    async deleteSchool(school_id) {
      const res = await FetchService.fetchData(`/schools/${school_id}`, "delete");
      if (res?.school_name) {
        this.schools.data.forEach((g) => {
          if (g.school_id === school_id) Object.assign(g, res);
        });
        return true;
      }
      return false;
    },
    async createDistrict(district: NewDistrict): Promise<{status: boolean; message?: string}>{
      const res = await FetchService.fetchData(`/districts`, "post", district, true);
      if (res.res.ok) {
        return { status: true, message: "Success" };
      } else {
        return { status: false, message: res.data };
      }
    },
    async createTrial(trial: Trial): Promise<{ status: boolean; message?: string }> {
      const res = await FetchService.fetchData(`/trial`, "post", trial, true);
      if (res.res.ok) {
        return { status: true, message: "Success" };
      } else {
        return { status: false, message: res.data };
      }
    },
    async updateDistrict(district_id: number, districtData: Partial<GetSupportDistrict>) {
      const res = await FetchService.fetchData(`/districts/${district_id}`, "put", districtData);
      if (res?.schools) {
        const dis = this.districts.data.find((i) => i.district_id == district_id);
        if (dis) {
          Object.assign(dis, res);
          return true;
        }
      }
      return false;
    },
    async deleteDistrict(district_id: number) {
      const res = await FetchService.fetchData(`/districts/${district_id}`, "delete");
      if (res?.schools) {
        const dis = this.districts.data.find((i) => i.district_id == district_id)!;
        if (dis) {
          Object.assign(dis, res);
          return true;
        }
      }
      return false;
    },

    async districtCreateSchool(
      school: DistrictCreateSchool
    ): Promise<{ status: boolean; message?: string }> {
      const res = await FetchService.fetchData(
        `/districts/school/${school.district_id}`,
        "post",
        school,
        true
      );
      if (res.res.ok) {
        this.schools.reload();
        this.districts.reload();
        return { status: true, message: "Success" };
      } else {
        return { status: false, message: res.data };
      }
    },

    // ------------------------------------------------------------------
    // QuickBooks actions
    // ------------------------------------------------------------------

    async getQbInvoices(school_id: number) {
      return FetchService.fetchData(`/quickbooks/invoices?school_id=${school_id}`, "get");
    },

    async searchQbCustomers(query: string, school_id?: number) {
      const params = new URLSearchParams();
      if (query) params.set("query", query);
      if (school_id) params.set("school_id", String(school_id));
      return FetchService.fetchData(`/quickbooks/customers/search?${params.toString()}`, "get");
    },

    async linkQbCustomer(school_id: number, qb_customer_id: string) {
      const res = await FetchService.fetchData("/quickbooks/link-school", "post", {
        school_id,
        qb_customer_id,
      });
      if (res?.status) {
        // Update local store so icon re-renders without a full reload
        const school = this.schools.data.find((s) => s.school_id === school_id);
        if (school?.status) school.status.ref_id = qb_customer_id;
      }
      return res;
    },

    async unlinkQbCustomer(school_id: number) {
      const res = await FetchService.fetchData("/quickbooks/unlink-school", "post", { school_id });
      if (res?.status) {
        const school = this.schools.data.find((s) => s.school_id === school_id);
        if (school?.status) school.status.ref_id = null;
      }
      return res;
    },

    async createQbCustomer(payload: {
      school_id?: number;
      display_name: string;
      company_name?: string;
      email?: string;
      phone?: string;
      address?: string;
      city?: string;
      state?: string;
      zip?: string;
    }) {
      const res = await FetchService.fetchData("/quickbooks/customers", "post", payload);
      if (res?.status && payload.school_id) {
        const school = this.schools.data.find((s) => s.school_id === payload.school_id);
        if (school?.status) school.status.ref_id = res.qb_customer_id;
      }
      return res;
    },
  }
});

export default useSchools;
